/**
 * Singleton ZITADEL UserManager. Lazily constructed on first access so
 * test runs in seed mode never instantiate it (and never throw on
 * unconfigured env).
 *
 * All callers go through the thin functions below — never `new UserManager`
 * directly — so the singleton invariant is enforceable and onUserChanged
 * subscriptions can be coordinated from one place.
 */

import { UserManager, type User } from "oidc-client-ts";
import { buildOidcSettings } from "./oidc-config";

let _manager: UserManager | null = null;

/**
 * Returns the singleton UserManager, constructing it (and wiring the
 * silent-renew failure handler) on first call.
 *
 * Silent-renew behaviour: when `automaticSilentRenew: true` is set in
 * `oidc-config.ts`, oidc-client-ts refreshes the access token in a hidden
 * iframe before it expires. If that fails (network blip, IdP down, refresh
 * token rotated), the User stays loaded but its access_token is stale —
 * the next Management API call returns `Errors.Token.Invalid` and the
 * facilitator sees a toast they can't act on. We bounce them through a
 * fresh `signinRedirect()` to recover. The `/auth/callback` guard avoids
 * an infinite redirect loop if the renew error fires mid-callback.
 */
export function getUserManager(): UserManager {
  if (_manager) return _manager;
  _manager = new UserManager(buildOidcSettings());
  _manager.events.addSilentRenewError((err) => {
    console.warn("Silent renew failed:", err);
    if (window.location.pathname === "/auth/callback") return;
    void _manager?.signinRedirect();
  });
  return _manager;
}

/**
 * Kick off the OIDC code+PKCE flow. The browser navigates away to ZITADEL's
 * authorize endpoint; this function does not return in the usual sense.
 *
 * `prompt: "select_account"` is deliberate: without it, a user-initiated
 * sign-in silently resumes whatever ZITADEL SSO session already exists, so
 * "logging in" as a second account (without a full logout in between) hands
 * back the FIRST user's tokens — making roles look crossed when you test the
 * seed accounts in one browser. Forcing the account chooser makes each
 * sign-in authenticate the account you actually intend.
 *
 * Note: only the explicit, user-initiated path passes this. Silent renew
 * (`signinSilent`) forces `prompt=none` internally, and the silent-renew
 * failure recovery in `getUserManager` re-auths the *same* user seamlessly —
 * neither should pop an account chooser, so both keep the default prompt.
 */
export async function signinRedirect(): Promise<void> {
  await getUserManager().signinRedirect({ prompt: "select_account" });
}

/**
 * Complete the redirect flow on the callback page. Reads `code` + `state`
 * from the URL, POSTs to the token endpoint, stores the resulting tokens
 * in sessionStorage, and returns the `User` (sub, profile, claims).
 *
 * Throws if `code`/`state` are missing or the token exchange fails — the
 * callback route surfaces the error.
 */
export async function signinCallback(): Promise<User> {
  return await getUserManager().signinCallback() as User;
}

/**
 * Read the current user from sessionStorage (or null if not signed in /
 * token expired). Synchronous-ish — oidc-client-ts marshals through a
 * promise but the data is already in storage.
 */
export async function getCurrentUser(): Promise<User | null> {
  return await getUserManager().getUser();
}

/**
 * Sign out. Bounces to ZITADEL's end_session endpoint with the id_token_hint
 * so the SSO session is killed too (not just the local tokens).
 */
export async function signoutRedirect(): Promise<void> {
  await getUserManager().signoutRedirect();
}

/**
 * Subscribe to "the signed-in user changed" — fires after signinCallback,
 * after silent renew, and on signout. Returns an unsubscribe function.
 */
export function onUserChanged(handler: (user: User | null) => void): () => void {
  const mgr = getUserManager();
  const loaded = (u: User) => handler(u);
  const unloaded = () => handler(null);
  mgr.events.addUserLoaded(loaded);
  mgr.events.addUserUnloaded(unloaded);
  mgr.events.addAccessTokenExpired(unloaded);
  return () => {
    mgr.events.removeUserLoaded(loaded);
    mgr.events.removeUserUnloaded(unloaded);
    mgr.events.removeAccessTokenExpired(unloaded);
  };
}
